import { useState } from "react";
import { createProduct, updateProduct } from "../lib/api.ts";
import type { Product } from "../lib/api.ts";

interface ProductFormModalProps {
  slug: string;
  /** Present → edit mode (prefilled); absent → add mode. */
  initial?: Product;
  onClose: () => void;
  onSaved: () => void;
}

const TYPE_OPTIONS = [
  { value: "retail", label: "Retail" },
  { value: "service_addon", label: "Service add-on" },
  { value: "part", label: "Part" },
];

export function ProductFormModal({ slug, initial, onClose, onSaved }: ProductFormModalProps) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name ?? "");
  // price_cents → display as dollars
  const [priceDollars, setPriceDollars] = useState(
    initial?.price_cents != null ? (initial.price_cents / 100).toFixed(2) : "",
  );
  const [type, setType] = useState(initial?.type ?? "retail");
  const [taxable, setTaxable] = useState(Boolean(initial?.taxable));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Product name is required.");
      return;
    }
    const dollars = parseFloat(priceDollars);
    if (isNaN(dollars) || dollars < 0) {
      setError("Enter a valid price (e.g. 12.50).");
      return;
    }
    const price_cents = Math.round(dollars * 100);

    setSubmitting(true);
    try {
      if (initial?.id) {
        await updateProduct(slug, initial.id, { name: name.trim(), price_cents, type, taxable });
      } else {
        await createProduct(slug, { name: name.trim(), price_cents, type, taxable });
      }
      setSubmitting(false);
      onSaved();
      onClose();
    } catch (err) {
      const msg =
        (err as { error?: { message?: string } })?.error?.message ??
        (err as Error)?.message ??
        "Could not save product. Please try again.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={submitting ? undefined : onClose}
      />

      {/* Sheet / dialog */}
      <form
        onSubmit={handleSubmit}
        className="relative w-full md:max-w-md rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{
          background: "var(--card-bg)",
          border: "1px solid var(--card-border)",
        }}
      >
        {/* Grab handle (mobile) */}
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: "var(--border)" }} />
        </div>

        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <h2 style={{ fontSize: "1.05rem", fontWeight: 600, color: "var(--text)" }}>
            {isEdit ? "Edit product" : "Add product"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-lg"
            style={{ color: "var(--text-muted)" }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hydrating Serum"
              autoComplete="off"
              autoFocus
            />
          </div>

          <div>
            <label className="label">Price (dollars)</label>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={priceDollars}
              onChange={(e) => setPriceDollars(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="label">Type</label>
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 2 }}>
            <input
              id="product-taxable"
              type="checkbox"
              checked={taxable}
              onChange={(e) => setTaxable(e.target.checked)}
              style={{ width: 16, height: 16, cursor: "pointer" }}
            />
            <label
              htmlFor="product-taxable"
              className="label"
              style={{ margin: 0, cursor: "pointer" }}
            >
              Taxable
            </label>
          </div>

          {error && (
            <div className="alert-error" style={{ marginTop: "0.25rem" }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn-secondary"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn-primary" style={{ flex: 1 }}>
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Add product"}
          </button>
        </div>
      </form>
    </div>
  );
}
